import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { FiArrowLeft, FiImage, FiPlus, FiX } from "react-icons/fi";
import { AuthContext } from "../../../Context/AuthContext";
import useProductCategories from "../../../Routes/Marketplace/Merchant/lib/fetchProductCategory";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import CategoryProductFields from "../components/CategoryProductFields";
import { getCategoryFormProfile } from "../utils/categoryFormConfig";
import {
  appendProductFormData,
  defaultProductForm,
  formatNgnPrice,
  MAX_PRODUCT_IMAGES,
  parsePriceInput,
  PRODUCT_FIELD_LIMITS,
  validateProductForm,
} from "../utils/productForm";

const IMAGE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";

const MerchantAddProductPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, token } = useContext(AuthContext);
  const autoVerify = process.env.REACT_APP_AUTO_VERIFY_MERCHANTS === "true";
  const canAddProduct = autoVerify || user?.isInfoVerify;

  const { data: categoryData, isLoading: categoriesLoading, isError: categoriesError } = useProductCategories();
  const categories = categoryData?.categories || [];

  const [form, setForm] = useState(defaultProductForm);
  const [priceDisplay, setPriceDisplay] = useState(formatNgnPrice(0));
  const [priceValue, setPriceValue] = useState(0);
  const [slashDisplay, setSlashDisplay] = useState("");
  const [slashValue, setSlashValue] = useState(0);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const profile = useMemo(() => getCategoryFormProfile(form.category), [form.category]);

  const previewUrls = useMemo(
    () => images.map((file) => URL.createObjectURL(file)),
    [images]
  );

  useEffect(
    () => () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    [previewUrls]
  );

  const handleCategoryChange = (category) => {
    setForm((prev) => ({
      ...defaultProductForm,
      category,
      name: prev.name,
      description: prev.description,
      quantity: prev.quantity,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "category") {
      handleCategoryChange(value);
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAttributeChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value },
    }));
  };

  const handlePriceChange = (e) => {
    const amount = parsePriceInput(e.target.value);
    setPriceValue(amount);
    setPriceDisplay(formatNgnPrice(amount));
  };

  const handleSlashPriceChange = (e) => {
    const raw = e.target.value;
    if (!raw.trim()) {
      setSlashValue(0);
      setSlashDisplay("");
      return;
    }
    const amount = parsePriceInput(raw);
    setSlashValue(amount);
    setSlashDisplay(formatNgnPrice(amount));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImages((prev) => [...prev, ...files].slice(0, MAX_PRODUCT_IMAGES));
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateProductForm({
      form,
      price: priceValue,
      compareAtPrice: slashValue || null,
      images,
    });
    if (validationErrors.length) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    setSubmitting(true);

    const body = new FormData();
    appendProductFormData(body, form, priceValue, slashValue);
    images.forEach((file) => body.append("images", file));

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/product/create/product`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (_) {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(payload.message || "Could not create product.");
      }

      queryClient.invalidateQueries("merchantProducts");
      toast.success(payload.message || "Product created successfully");
      navigate("/merchant/products");
    } catch (err) {
      setErrors([err.message || "Could not create product. Please try again."]);
      toast.error(err.message || "Could not create product");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canAddProduct) {
    return (
      <>
        <PageHeader title="Add product" subtitle="Create a new listing" icon={FiPlus} />
        <EmptyState
          icon={FiPlus}
          title="Verification required"
          message="Complete your business information before you can list products."
          actionLabel="Complete profile"
          actionTo="/merchant/account"
        />
      </>
    );
  }

  return (
    <>
      <Link to="/merchant/products" className="dashboard-back-link">
        <FiArrowLeft aria-hidden="true" /> Back to products
      </Link>

      <PageHeader
        title="Add product"
        subtitle="Choose a category first — the form adapts to your product type"
        icon={FiPlus}
      />

      {errors.length > 0 && (
        <div className="dashboard-banner dashboard-banner--error" role="alert">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="dashboard-product-form">
        <section className="dashboard-card dashboard-category-picker">
          <h2 className="dashboard-card__title">Category *</h2>
          <p className="dashboard-form-hint">
            What you sell determines which fields appear — electronics get specs, fashion gets sizes, groceries get weight, and so on.
          </p>

          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            required
            disabled={categoriesLoading || categoriesError}
            className="dashboard-category-picker__select"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat.categoryName}>
                {cat.categoryName}
              </option>
            ))}
          </select>

          {categoriesError && (
            <p className="dashboard-form-hint dashboard-form-hint--error">
              Could not load categories. Refresh the page and try again.
            </p>
          )}

          {form.category && (
            <div className="dashboard-category-chip">
              Listing as: <strong>{profile.title}</strong>
            </div>
          )}
        </section>

        {!form.category && (
          <div className="dashboard-card dashboard-category-empty">
            <p>Select a category above to continue with product details.</p>
          </div>
        )}

        {form.category && (
          <>
            <section className="dashboard-card">
              <h2 className="dashboard-card__title">Photos *</h2>
              <p className="dashboard-form-hint">
                Up to {MAX_PRODUCT_IMAGES} images (JPEG, PNG, GIF, or WebP, max 5 MB each).
              </p>

              <div className="dashboard-image-upload-grid">
                {previewUrls.map((url, index) => (
                  <div key={url} className="dashboard-image-upload-preview">
                    <img src={url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="dashboard-image-upload-remove"
                      onClick={() => removeImage(index)}
                      aria-label="Remove image"
                    >
                      <FiX />
                    </button>
                  </div>
                ))}

                {images.length < MAX_PRODUCT_IMAGES && (
                  <label className="dashboard-image-upload-slot">
                    <FiImage aria-hidden="true" />
                    <span>{images.length ? "Add more" : "Upload images"}</span>
                    <input type="file" accept={IMAGE_ACCEPT} multiple onChange={handleImageChange} />
                  </label>
                )}
              </div>
            </section>

            <section className="dashboard-card">
              <h2 className="dashboard-card__title">Basic info</h2>
              <div className="dashboard-form-grid">
                <label className="dashboard-form-field dashboard-form-field--full">
                  <span>
                    Product name *
                    <span className="dashboard-char-count">
                      {form.name.length}/{PRODUCT_FIELD_LIMITS.name}
                    </span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={profile.namePlaceholder}
                    maxLength={PRODUCT_FIELD_LIMITS.name}
                    required
                  />
                </label>

                <label className="dashboard-form-field">
                  <span>Selling price (NGN) *</span>
                  <input type="text" value={priceDisplay} onChange={handlePriceChange} inputMode="numeric" required />
                </label>

                <label className="dashboard-form-field">
                  <span>Slash price (was)</span>
                  <input
                    type="text"
                    value={slashDisplay}
                    onChange={handleSlashPriceChange}
                    inputMode="numeric"
                    placeholder="Optional — original price before discount"
                  />
                </label>

                {!profile.showSizes && (
                  <label className="dashboard-form-field">
                    <span>Stock quantity</span>
                    <input
                      type="number"
                      name="quantity"
                      value={form.quantity}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                    />
                  </label>
                )}
              </div>
            </section>

            <CategoryProductFields
              profile={profile}
              form={form}
              onChange={handleChange}
              onAttributeChange={handleAttributeChange}
            />

            <section className="dashboard-card">
              <h2 className="dashboard-card__title">Description *</h2>
              <label className="dashboard-form-field dashboard-form-field--full">
                <span>
                  Description *
                  <span className="dashboard-char-count">
                    {form.description.length}/{PRODUCT_FIELD_LIMITS.description}
                  </span>
                </span>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={6}
                  placeholder={profile.descriptionPlaceholder}
                  maxLength={PRODUCT_FIELD_LIMITS.description}
                  required
                />
              </label>
            </section>

            <div className="dashboard-form-actions">
              <Link to="/merchant/products" className="dashboard-btn dashboard-btn--secondary">
                Cancel
              </Link>
              <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={submitting}>
                {submitting ? "Saving…" : "Save product"}
              </button>
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default MerchantAddProductPage;
